import { Home, BookOpen, Award, Users, Library, Star, FileText, User, LogOut, Shield, GraduationCap, FolderOpen } from 'lucide-react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { getUserName } from '../../shared/utils/userAdapter';

const menuItems = [
  { icon: Home, label: 'Minha Home', path: '/' },
  { icon: BookOpen, label: 'Meus Conteúdos', path: '/meus-conteudos' },
  { icon: Award, label: 'Trilhas', path: '/trilhas' },
  { icon: Users, label: 'Turmas', path: '/turmas' },
  { icon: Library, label: 'Biblioteca', path: '/biblioteca' },
  { icon: BookOpen, label: 'Catálogo de Cursos', path: '/catalogo' },
  { icon: Star, label: 'Favoritos', path: '/favoritos' },
  { icon: FileText, label: 'Anotações', path: '/anotacoes' },
];

export function MainLayout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { canViewAdminPanel, isInstrutor } = usePermissions();

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-primary-600">
            Universidade Salt
          </h1>
          <p className="text-sm text-gray-500 mt-1">Portal de Treinamento</p>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
          
          {/* Instructor Section */}
          {isInstrutor && (
            <>
              <div className="pt-4 pb-2 mt-4 border-t border-gray-200">
                <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Instrutor
                </p>
              </div>
              <Link
                to="/instructor"
                className={`sidebar-item ${location.pathname.startsWith('/instructor') ? 'sidebar-item-active' : ''}`}
              >
                <GraduationCap size={20} />
                <span>Painel Instrutor</span>
              </Link>
            </>
          )}
          
          {/* Admin Section */}
          {canViewAdminPanel && (
            <>
              <div className="pt-4 pb-2 mt-4 border-t border-gray-200">
                <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Administração
                </p>
              </div>
              <Link
                to="/admin"
                className={`sidebar-item ${location.pathname === '/admin' ? 'sidebar-item-active' : ''}`}
              >
                <Shield size={20} />
                <span>Painel Admin</span>
              </Link>
              <Link
                to="/admin/bibliotecas"
                className={`sidebar-item ${location.pathname === '/admin/bibliotecas' ? 'sidebar-item-active' : ''}`}
              >
                <FolderOpen size={20} />
                <span>Bibliotecas de Docs</span>
              </Link>
            </>
          )}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-gray-200">
          <Link to="/perfil" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={getUserName(user)} className="w-full h-full rounded-full object-cover" />
              ) : (
                <User size={20} className="text-primary-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {getUserName(user)}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email}
              </p>
            </div>
          </Link>
          
          <button
            onClick={() => logout()}
            className="w-full flex items-center gap-3 px-3 py-2 mt-2 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm">Sair</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
